import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  Typography,
  IconButton
} from '@mui/material'
import {
  Close,
  Person,
  Business,
  Email,
  Phone,
  LocationOn
} from '@mui/icons-material'

import { useCreateClient, useUpdateClient } from '../../hooks/useClients'

// Validation schema
const clientSchema = yup.object({
  name: yup
    .string()
    .required('El nombre es requerido')
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(200, 'El nombre no puede exceder 200 caracteres'),
  rut: yup
    .string()
    .required('El RUT es requerido')
    .matches(/^[0-9]{1,2}\.?[0-9]{3}\.?[0-9]{3}[-|‐]{1}[0-9kK]{1}$/, 'Formato de RUT inválido (ej: 12345678-9 o 12.345.678-9)'),
  email: yup
    .string()
    .email('Email inválido')
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value),
  phone: yup
    .string()
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value),
  address: yup
    .string()
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value),
  paymentTermsDays: yup
    .number()
    .required('El plazo de pago es requerido')
    .integer('Debe ser un número entero')
    .min(1, 'El plazo mínimo es 1 día')
    .max(365, 'El plazo máximo es 365 días')
})

const ClientForm = ({ 
  open, 
  onClose, 
  client = null, 
  title = null 
}) => {
  const isEdit = !!client
  const createClientMutation = useCreateClient()
  const updateClientMutation = useUpdateClient()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(clientSchema),
    defaultValues: {
      name: '',
      rut: '',
      email: '',
      phone: '',
      address: '',
      paymentTermsDays: 30
    }
  })

  // Update form values when client changes
  useEffect(() => {
    if (client) {
      setValue('name', client.name || '')
      setValue('rut', client.rut || '')
      setValue('email', client.email || '')
      setValue('phone', client.phone || '')
      setValue('address', client.address || '')
      setValue('paymentTermsDays', client.paymentTermsDays || 30)
    } else {
      reset({
        name: '',
        rut: '',
        email: '',
        phone: '',
        address: '',
        paymentTermsDays: 30
      })
    }
  }, [client, setValue, reset])

  const isLoading = createClientMutation.isLoading || updateClientMutation.isLoading

  const onSubmit = async (data) => {
    try {
      if (isEdit) {
        await updateClientMutation.mutateAsync({
          id: client.id,
          ...data
        })
      } else {
        await createClientMutation.mutateAsync(data)
      }
      handleClose()
    } catch (error) {
      console.error('Error saving client:', error)
    }
  }

  const handleClose = () => {
    reset({
      name: '',
      rut: '',
      email: '',
      phone: '',
      address: ''
    })
    onClose()
  }

  const formatRUT = (value) => {
    // If the value already has the correct format, keep it
    if (/^[0-9]{1,2}\.?[0-9]{3}\.?[0-9]{3}[-|‐]{1}[0-9kK]{1}$/.test(value)) {
      return value
    }
    
    // Remove any non-alphanumeric characters except dots
    const cleaned = value.replace(/[^0-9kK.]/g, '')
    
    // If it has dots, preserve them and just add hyphen
    if (cleaned.includes('.')) {
      const parts = cleaned.split('.')
      if (parts.length >= 3 && parts[2].length > 1) {
        const lastPart = parts[2]
        const rutBody = parts.slice(0, 2).join('.') + '.' + lastPart.slice(0, -1)
        const dv = lastPart.slice(-1)
        return rutBody + '-' + dv
      }
      return cleaned
    }
    
    // Simple format without dots
    const numbersOnly = cleaned.replace(/[^0-9kK]/g, '')
    if (numbersOnly.length > 1) {
      return numbersOnly.slice(0, -1) + '-' + numbersOnly.slice(-1)
    }
    
    return numbersOnly
  }

  const handleRUTChange = (event) => {
    const formatted = formatRUT(event.target.value)
    event.target.value = formatted
    setValue('rut', formatted)
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person color="primary" />
            <Typography variant="h6">
              {title || (isEdit ? 'Editar Cliente' : 'Nuevo Cliente')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Name */}
            <Grid item xs={12} md={8}>
              <TextField
                {...register('name')}
                fullWidth
                label="Nombre o Razón Social"
                error={!!errors.name}
                helperText={errors.name?.message}
                disabled={isLoading}
                InputProps={{
                  startAdornment: <Business sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>

            {/* RUT */}
            <Grid item xs={12} md={4}>
              <TextField
                {...register('rut')}
                fullWidth
                label="RUT"
                placeholder="12345678-9"
                error={!!errors.rut}
                helperText={errors.rut?.message}
                disabled={isLoading}
                onChange={handleRUTChange}
                inputProps={{ maxLength: 12 }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <TextField
                {...register('email')}
                fullWidth
                label="Email"
                type="email"
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={isLoading}
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} md={6}>
              <TextField
                {...register('phone')}
                fullWidth
                label="Teléfono"
                placeholder="+56 9 1234 5678"
                error={!!errors.phone}
                helperText={errors.phone?.message}
                disabled={isLoading}
                InputProps={{
                  startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>

            {/* Payment Terms */}
            <Grid item xs={12} md={6}>
              <TextField
                {...register('paymentTermsDays')}
                fullWidth
                label="Plazo de Pago (días)"
                type="number"
                placeholder="30"
                error={!!errors.paymentTermsDays}
                helperText={errors.paymentTermsDays?.message || 'Días que tiene el cliente para pagar las facturas'}
                disabled={isLoading}
                InputProps={{
                  inputProps: { min: 1, max: 365 }
                }}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <TextField
                {...register('address')}
                fullWidth
                label="Dirección"
                multiline
                rows={2}
                error={!!errors.address}
                helperText={errors.address?.message}
                disabled={isLoading}
                InputProps={{
                  startAdornment: <LocationOn sx={{ mr: 1, color: 'action.active', alignSelf: 'flex-start', mt: 1 }} />
                }}
              />
            </Grid>
          </Grid>

          {/* Preview */}
          {watch('name') && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Vista previa:
              </Typography>
              <Typography variant="h6">
                {watch('name')}
              </Typography>
              {watch('rut') && (
                <Typography variant="body2" color="text.secondary">
                  RUT: {watch('rut')}
                </Typography>
              )}
              {(watch('email') || watch('phone')) && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  {watch('email') && (
                    <Typography variant="caption">
                      📧 {watch('email')}
                    </Typography>
                  )}
                  {watch('phone') && (
                    <Typography variant="caption">
                      📞 {watch('phone')}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleClose}
            disabled={isLoading}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            sx={{ minWidth: 120 }}
          >
            {isLoading ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Crear Cliente')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default ClientForm
